import { useMutation, useQuery, useQueryClient, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCurrentUser } from '@/hooks/api/use-user';
import { pushGoogleEvent } from '@/lib/analytics';
import { ApiError } from '@/lib/api/api-error';
import { getApiErrorMessage } from '@/lib/api/client';
import { getData, postData, postFormData, putData } from '@/lib/api/client-v2';
import { DATE_FR, DATE_ISO, formatDate, isValidDate, toDate, type DateInput } from '@/lib/dates';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import { useUiStore } from '@/stores/ui-store';
import type { Accident, AccidentPreviewV2, PostAccidentResponse } from '@/types/api/accident';
import { ENDPOINTS } from '@/types/api/endpoints';
import type { FleetGarantie, FleetVehicle } from '@/types/api/fleet';

type Id = string | number | null | undefined;

const hasId = (id: Id) => id !== null && id !== undefined && id !== '';

interface AccidentsQueryOptions {
    /**
     * `'always'` : relit la liste à chaque montage (ancien bloc d'accueil et iframe de déclaration,
     * qui relançaient le fetch sans condition). Par défaut, la liste n'est lue qu'une fois
     * (ancien `_.isEmpty(accidents) ? fetchAccidents() : null`).
     */
    refetchOnMount?: boolean | 'always';
    enabled?: boolean;
}

/* ---------- Lectures ---------- */

/**
 * Ramène un sinistre des listes v2 aux noms de la v1, que lisent les écrans. Le détail
 * (`useAccident`) garde, lui, la projection v1 telle quelle.
 */
export const toAccident = (accident: AccidentPreviewV2): Accident => ({
    id: accident.id,
    id_contrat: accident.contract_id ?? '',
    numCbt: accident.claim_number ?? undefined,
    libelle: accident.label ?? undefined,
    situation: accident.situation ?? undefined,
    situationCode: accident.situation_code ?? undefined,
    categorie: accident.category ?? undefined,
    categorieCode: accident.category_code ?? undefined,
    dateSurv: accident.occurred_at ?? undefined,
    dateOuverture: accident.opened_at ?? undefined,
    tauxResp: accident.responsibility_rate ?? undefined,
    type: accident.type ?? undefined,
    reglements: [],
});

/** Sinistres du tiers connecté (`GET /tiers/{id}/accidents`, `{id}` = `belair_id`). */
export const useTiersAccidents = ({ refetchOnMount, enabled = true }: AccidentsQueryOptions = {}) => {
    const tiersId = useCurrentUser().belair_id;

    return useQuery({
        queryKey: queryKeys.accidentsTiers(tiersId),
        queryFn: () => getData<AccidentPreviewV2[]>(ENDPOINTS.tiersAccidents(String(tiersId))).then((accidents) => accidents.map(toAccident)),
        staleTime: Infinity,
        ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
        enabled,
    });
};

/** Liste des sinistres du tiers, vide tant qu'elle n'est pas chargée (ancien `getAccidentsTiersData`). */
export const useTiersAccidentsData = (options?: AccidentsQueryOptions): Accident[] =>
    useTiersAccidents(options).data ?? [];

/** Sinistres d'un contrat (`GET /contracts/{id}/accidents`). */
export const useContractAccidents = (contractId: Id, { refetchOnMount, enabled = true }: AccidentsQueryOptions = {}) =>
    useQuery({
        queryKey: queryKeys.accidentsContract(contractId),
        queryFn: () =>
            getData<AccidentPreviewV2[]>(ENDPOINTS.contractAccidents(contractId as string | number)).then((accidents) =>
                accidents.map(toAccident),
            ),
        staleTime: Infinity,
        ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
        enabled: enabled && hasId(contractId),
    });

/**
 * Détail d'un sinistre (`GET /contracts/{id}/accidents/{id}`, v2 déléguée à la v1 : même
 * projection, dans `data`).
 *
 * L'ancienne fiche relisait le sinistre à chaque ouverture : `enabled: false` permet de lire le
 * cache seul (fil d'Ariane).
 */
export const useAccident = (contractId: Id, accidentId: Id, enabled = true) =>
    useQuery({
        queryKey: queryKeys.accident(contractId, accidentId),
        queryFn: () => getData<Accident>(ENDPOINTS.contractAccident(contractId as string | number, accidentId as string | number)),
        refetchOnMount: 'always',
        enabled: enabled && hasId(contractId) && hasId(accidentId),
    });

/** Pièces retirées de l'affichage tant qu'elles ne sont pas marquées dans BELAIR (J, 6, 0, E, 4, D). */
export const getDisplayedAccidentDocuments = (accident?: Accident) =>
    (accident?.documents ?? []).filter((doc) => !['J', '6', '0', 'E', '4', 'D'].includes(doc.code));

/* ---------- Déclaration ---------- */

type Choice = string | undefined;

/** Valeurs du formulaire de déclaration, telles que les champs les remplissent. */
export interface DeclarationSinistreValues {
    type_sinistre?: string;
    catsinorigin: string;
    commentaire?: string;
    motif_deplacement?: string;
    datesurvorigin?: Date | string | null;
    heuresurvorigin?: string;
    pays_lieu_sinistre: string;
    pays: string;
    lieu?: string;
    cplieu?: string;
    corpoconduct?: Choice;
    birthdate_conduct?: Date | string | null;
    conduct?: string;
    risque?: string;
    temoin?: Choice;
    nb_vehicules?: string;
    indentify?: Choice;
    vehiculeDommage?: Choice;
    equipementsDommage?: Choice;
    equipementsDommageCasque?: Choice;
    equipementsDommageGants?: Choice;
    equipementsDommageBlouson?: Choice;
    equipementsDommageBottes?: Choice;
    equipementsDommageAirbag?: Choice;
    equipementsDommageAutre?: Choice;
    equipementsDommageAutreDesc?: string;
    dommagesDeclares?: string;
    vehiculeIsInGarage?: Choice;
    vehiculeIsMark?: Choice;
    vehiculeIsAttached?: Choice;
    hasPV?: Choice;
    hasAggression?: Choice;
    constat_accident?: Choice;
    constat_sign_tiers?: Choice;
    constat_sign_assure?: Choice;
    intervention_police?: Choice;
    document_police?: string;
    blesse?: Choice;
    point_choc?: string;
    vehicule?: Choice;
    reparateur?: string;
    reparateurAutreNom?: string;
    reparateurAutreAdresse?: string;
    cpreparateur?: string;
    reparateurAutreVille?: string;
    visit?: string;
    remb_garage?: Choice;
    iban?: string;
    domiciliation?: string;
    bic?: string;
    codegta?: string;
    documents?: { description?: string }[];
    /** Pièces jointes par code (initialisé à `[]`, puis complété en objet par les champs fichier). */
    files: Record<string, File | undefined>;
    adversaires: Record<string, string | undefined>[];
    temoins: Record<string, string | undefined>[];
    victimes: Record<string, string | undefined>[];
    /** Cases cochées du constat (initialisé à `[]`, puis complété en objet `coche_aN` / `coche_bN`). */
    circonstances: Record<string, boolean>;
    client_email: string;
    client_phone: string;
    client_password: string;
    client_contact_validated: boolean;
    [key: string]: unknown;
}

/**
 * Corps `multipart` d'une déclaration, construit exactement comme l'ancien middleware
 * `POST_ACCIDENT` (dates au format `L`, catégorie scindée, autre garage, pièces mises à plat).
 */
export const buildAccidentFormData = (body: DeclarationSinistreValues): FormData => {
    const data = new FormData();
    /* Copie de surface : seules les clés de premier niveau sont modifiées ci-dessous. */
    const newValues = { ...body } as Record<string, unknown>;

    /* datesurv veut une date en / ; datesurvorigin est une date */
    newValues.datesurv = formatDate(newValues.datesurvorigin as DateInput);
    /* catsinorigin est découpé parce que le select de la catégorie est très précis */
    const arrayCategory = String(newValues.catsinorigin).split('.');
    newValues.catsin = arrayCategory[0];
    newValues.catsinsubcategory = arrayCategory[1];

    if (newValues.birthdate_conduct) {
        newValues.birthdate_conduct = formatDate(newValues.birthdate_conduct as DateInput);
    }

    /* Gestion si un autre garage a été saisi */
    if (newValues.reparateur) {
        if (newValues.reparateur === 'NONE') {
            newValues.reparateur_autre = `${String(newValues.reparateurAutreNom)} ${String(newValues.reparateurAutreAdresse)}, ${String(newValues.cpreparateur)} ${String(newValues.reparateurAutreVille)}`;
            delete newValues.reparateur;
        }
    }

    /* Envoi de la liste des documents pour le mail */
    if (body.documents) {
        body.documents.forEach((doc, index) => {
            data.append(`documents[${index}]`, String(doc.description));
        });
    }

    /* Mise à plat des files, adversaires, témoins, victimes */
    const source = body as Record<string, unknown>;
    Object.keys(source).forEach((key) => {
        if (key === 'files') {
            const files = source[key] as Record<string, File>;
            Object.keys(files).forEach((doc, index) => {
                const file = files[doc];
                if (file.name !== undefined) {
                    data.append(`files[${doc}]`, file);
                    data.append(`files_meta[${index}][filename]`, file.name);
                    data.append(`files_meta[${index}][size]`, String(file.size));
                    data.append(`files_meta[${index}][mimetype]`, file.type);
                }
            });
        } else if (key === 'adversaires' || key === 'temoins' || key === 'victimes') {
            (source[key] as Record<string, unknown>[]).forEach((person, index) => {
                Object.keys(person).forEach((info) => {
                    data.append(`${key}[${index}][${info}]`, person[info] as string);
                });
            });
        } else if (key === 'circonstances') {
            Object.keys(newValues[key] as object).forEach((circ) => {
                data.append(`${key}[${circ}]`, 'true');
            });
        }
        return true;
    });

    Object.keys(newValues).forEach((key) => {
        if (key !== 'files' && key !== 'adversaires' && key !== 'temoins' && key !== 'victimes' && key !== 'circonstances') {
            /* react-hook-form conserve les champs à undefined/null (ex: manuel_resp non calculé).
               FormData.append() les sérialiserait en "undefined"/"null" et le back les rejette
               (ex: taux_resp doit être un entier). On ne les envoie donc pas. */
            if (newValues[key] === undefined || newValues[key] === null) {
                return;
            }
            /* manuel_resp (mappé sur taux_resp) et manuel_ida ne sont jamais transmis : sur une
               déclaration faite par le client, c'est au gestionnaire de fixer la responsabilité. */
            if (key === 'manuel_resp' || key === 'manuel_ida') {
                return;
            }
            data.append(key, newValues[key] as string);
        }
    });

    return data;
};

export interface PostAccidentVariables {
    form: DeclarationSinistreValues;
    contractId: string | number;
    successFunction?: () => void;
}

/** Déclaration de sinistre (`POST /contracts/{id}/accidents`, multipart). */
/**
 * Message d'erreur d'un envoi de sinistre. Sans réponse du serveur (réseau coupé, timeout, 413 du
 * proxy sans en-têtes CORS), le message générique n'aide pas : on oriente vers la taille des pièces.
 */
export const getPostAccidentErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError && error.httpStatus === 0) {
        return 'Impossible de joindre le serveur. Vérifiez votre connexion et la taille de vos pièces jointes (10 Mo maximum par fichier), puis réessayez.';
    }
    return getApiErrorMessage(error) || 'Merci de réessayer ultérieurement.';
};

export const usePostAccident = () => {
    const setPostAccidentResponse = useUiStore((state) => state.setPostAccidentResponse);

    return useMutation({
        mutationFn: ({ form, contractId }: PostAccidentVariables) => {
            pushGoogleEvent({ event: 'click', actionGoogle: 'clickButton', libelle: 'Déclaration de Sinistre' });

            return postFormData<PostAccidentResponse | null>(ENDPOINTS.contractAccidents(contractId), buildAccidentFormData(form));
        },
        onSuccess: (envelope, { successFunction }) => {
            const message = envelope.message || 'Vos informations seront mises à jour sous 24h';

            void notify({
                icon: 'success',
                title: message,
                confirmButtonText: "J'ai compris",
                successFunction,
            });
            setPostAccidentResponse(envelope.data ?? undefined);
        },
        onError: (error) => {
            void notify({
                html: '<p>Une erreur est survenue : ' + getPostAccidentErrorMessage(error) + '</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });
};

/**
 * Refus automatique d'une déclaration (MAE), notifié au client par email
 * (`POST /contracts/{id}/accidents/rejection {cause}` ; la v1 nommait le motif `categorie`).
 */
export const usePostRejectAccident = () =>
    useMutation({
        mutationFn: ({ contractId, cause }: { contractId: string | number; cause: string }) =>
            postData(ENDPOINTS.contractAccidentRejection(contractId), { cause }),
        onSuccess: () => {
            void notify({
                html: '<p>Une notification du refus vient de vous être envoyée par email.</p>',
                type: 'error',
                title: 'Attention !',
            } as Parameters<typeof notify>[0]);
        },
        onError: (error) => {
            void notify({
                html: '<p>Une erreur est survenue : ' + getApiErrorMessage(error) + '</p>',
                icon: 'error',
                title: 'Attention !',
            });
        },
    });

/* ---------- Réparateur ---------- */

export interface AjoutReparateurValues {
    reparateur: string;
    cpreparateur: string;
    visit: string;
    [key: string]: unknown;
}

/**
 * Corps v2 du choix de réparateur : `repairer_id` entier, `visit_date` en `Y-m-d` (le formulaire
 * la tient en jj/mm/aaaa, format de la v1), `null` sans créneau. Le code postal ne servait qu'à
 * la recherche : la v2 ne le reçoit plus.
 */
export const toRepairerBody = (form: AjoutReparateurValues) => ({
    repairer_id: Number(form.reparateur),
    visit_date: form.visit && isValidDate(form.visit, DATE_FR) ? formatDate(toDate(form.visit, DATE_FR), DATE_ISO) : null,
});

/** Ajout d'un réparateur sur un sinistre ouvert (`PUT /accidents/{id}/repairer`). */
export const usePostAccidentRepairer = () =>
    useMutation({
        mutationFn: ({ accidentId, form }: { accidentId: string | number; form: AjoutReparateurValues; successFunction?: () => void }) =>
            putData(ENDPOINTS.accidentRepairer(accidentId), toRepairerBody(form)),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<p>Vos informations seront mises à jour sous 24h</p>',
                type: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as Parameters<typeof notify>[0]);
        },
        onError: (error) => {
            void notify({
                html: '<p>Une erreur est survenue : ' + getPostAccidentErrorMessage(error) + '</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });

/* ---------- Garanties d'un contrat flotte ---------- */

/** Clé de la lecture `useFleetVehicle` la plus récente du cache (égalité : la première l'emporte). */
const findLastFleetVehicleKey = (queryClient: QueryClient): QueryKey | undefined =>
    queryClient
        .getQueryCache()
        .findAll({ queryKey: ['fleet', 'detail'] })
        .filter((query) => query.state.data !== undefined)
        .reduce<Query | undefined>((best, query) => (!best || query.state.dataUpdatedAt > best.state.dataUpdatedAt ? query : best), undefined)
        ?.queryKey;

/** Clé neutre, jamais lue par `useFleetVehicle`, quand aucun véhicule n'a été consulté. */
const NO_FLEET_VEHICLE_KEY = ['fleet', 'detail', null] as const;

/**
 * Garanties du dernier véhicule de flotte consulté (ancien `getFlotteDetailData(state)?.garanties`).
 *
 * Redux gardait en mémoire le dernier `fleet.get` chargé ; on retient ici la plus récente des
 * lectures `useFleetVehicle` présentes dans le cache et on s'y abonne, sans la relire : tant que
 * l'écran est monté, la lecture a un observateur et le cache ne la libère pas (sans lui, elle
 * disparaissait au bout de `gcTime`, 5 min, en plein formulaire de déclaration).
 */
export const useLastFleetVehicleGaranties = (enabled: boolean): FleetGarantie[] | undefined => {
    const queryClient = useQueryClient();
    const lastKey = useMemo(() => (enabled ? findLastFleetVehicleKey(queryClient) : undefined), [enabled, queryClient]);

    const { data } = useQuery({
        queryKey: lastKey ?? NO_FLEET_VEHICLE_KEY,
        enabled: false,
        select: (vehicle: FleetVehicle) => vehicle.garanties,
    });

    return enabled ? data : undefined;
};
